import { useQuery } from '@tanstack/react-query';
import { getRecipeFonts, type WorkspaceCustomFontDto } from '@/api/recipes';

/** Invalidate on sign-out via `queryClient.removeQueries({ queryKey: RECIPE_FONTS_QUERY_KEY_ROOT })`. */
export const RECIPE_FONTS_QUERY_KEY_ROOT = ['recipe-fonts'] as const;

/**
 * Maps `resource_key` and `originalFileName` to the signed URL for the font file.
 * We do both keys because for backwards compatibility, originalFileName is still
 * used by pre-`Font::Custom` saved workflows.
 */
export function buildWorkspaceCustomFontsUrlsMap(fonts: WorkspaceCustomFontDto[]): ReadonlyMap<string, string> {
  const m = new Map<string, string>();
  for (const f of fonts) {
    m.set(f.id, f.url);
    if (f.originalFileName) {
      m.set(f.originalFileName, f.url);
    }
  }
  return m;
}

type useWorkflowFontsQueryOptions = {
  enabled: boolean;
};

/**
 * GET `/v1/recipes/{recipeId}/fonts`. Disabled when `recipeId` is missing or `enabled` is false.
 */
export function useWorkflowFontsQuery(recipeId: string | undefined, options: useWorkflowFontsQueryOptions) {
  return useQuery({
    queryKey: [...RECIPE_FONTS_QUERY_KEY_ROOT, recipeId],
    queryFn: () => getRecipeFonts(recipeId!),
    enabled: Boolean(recipeId) && options.enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
